import {useMemo, useLayoutEffect, useRef, useState, type CSSProperties} from "react";
import {Background, Controls, MarkerType, Position, ReactFlow, type Edge, type Node} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {OtsBadge} from "../components/OtsBadge";
import {DecisionMessage, type DecisionMessageProps} from "./DecisionMessage";
import {richMessagePlainText} from "../diagnostics/RichMessageText";
import type {DecisionGraphNode} from "./decision-graph-model";
import type {DecisionGraphModel} from "./decision-graph-model";

export interface DecisionGraphProps {
  model: DecisionGraphModel;
  label?: string;
  description?: string;
  canNavigate?: DecisionMessageProps["canNavigate"];
  onNavigate?: DecisionMessageProps["onNavigate"];
}

type MessageOptions = Pick<DecisionMessageProps, "expanded" | "canNavigate" | "onNavigate">;
const nodeLines = (node: DecisionGraphNode, expanded = false) => node.messages?.length
  ? node.messages.flatMap(message => [richMessagePlainText(message),
    ...(expanded && message.detail ? [richMessagePlainText(message, true)] : []),
    ...(expanded ? (message.facts ?? []).map(fact => `${fact.label ?? "Supporting input"}: ${fact.value?.display ?? ""} ${fact.value?.unit ?? ""}`) : [])])
  : node.lines;

/** Layout uses connections, never module-specific keys or decision codes. */
export const buildDecisionGraph = (model: DecisionGraphModel, compact = false, options: MessageOptions = {}) => {
  const byId = new Map(model.nodes.map(node => [node.id, node]));
  if (byId.size !== model.nodes.length) throw new Error("Decision graph node keys must be unique.");
  const incoming = new Map(model.nodes.map(node => [node.id, 0]));
  const outgoing = new Map(model.nodes.map(node => [node.id, [] as string[]]));
  const connections = new Set<string>();
  model.edges.forEach(edge => {
    if (!byId.has(edge.source) || !byId.has(edge.target)) throw new Error("Decision graph connections must reference existing nodes.");
    const key = JSON.stringify([edge.source, edge.target]);
    if (connections.has(key)) throw new Error("Decision graph connections must be unique.");
    connections.add(key);
    incoming.set(edge.target, incoming.get(edge.target)! + 1);
    outgoing.get(edge.source)!.push(edge.target);
  });
  const roots = model.nodes.filter(node => incoming.get(node.id) === 0).map(node => node.id);
  const queue = [...roots];
  const depth = new Map(roots.map(id => [id, 0]));
  const order: string[] = [];
  while (queue.length) {
    const id = queue.shift()!;
    order.push(id);
    outgoing.get(id)!.forEach(target => {
      depth.set(target, Math.max(depth.get(target) ?? 0, depth.get(id)! + 1));
      incoming.set(target, incoming.get(target)! - 1);
      if (incoming.get(target) === 0) queue.push(target);
    });
  }
  if (order.length !== model.nodes.length) throw new Error("Decision graphs must not contain circular connections.");
  // Visit each branch immediately, but defer shared results until all their inputs are placed.
  const compactOrder: string[] = [];
  const placed = new Set<string>();
  const visit = (id: string) => {
    if (placed.has(id) || model.edges.some(edge => edge.target === id && !placed.has(edge.source))) return;
    placed.add(id);
    compactOrder.push(id);
    outgoing.get(id)!.forEach(visit);
  };
  roots.forEach(visit);
  const rows = new Map<number, string[]>();
  order.forEach(id => rows.set(depth.get(id)!, [...(rows.get(depth.get(id)!) ?? []), id]));
  const height = (id: string) => {
    const node = byId.get(id)!;
    return Math.max(140, 90 + [node.title, ...nodeLines(node, options.expanded)].reduce((sum, line) => sum + Math.ceil(line.length / (compact ? 28 : node.kind === "reason" ? 48 : 36)) * 20 + 12, 0));
  };
  const positions = new Map<string, {x: number; y: number}>();
  let y = 0;
  if (compact) compactOrder.forEach(id => { positions.set(id, {x: 40, y}); y += height(id); });
  else {
    const columns = Math.max(1, ...Array.from(rows.values(), row => row.length));
    rows.forEach(row => {
      row.forEach((id, index) => positions.set(id, {x: (columns - row.length) * 165 + index * 330 - (byId.get(id)!.kind === "reason" ? 60 : 0), y}));
      y += Math.max(...row.map(height));
    });
  }
  const nodes: Node[] = model.nodes.map(node => {
    const tone = node.status === "selected" ? "success" : node.status === "rejected" ? "danger" : "neutral";
    // Side handles keep long connections outside cards in the stacked layout.
    const sourceSide = compact && (outgoing.get(node.id)!.length > 1 || outgoing.get(node.id)!.some(target => positions.get(target)!.y > positions.get(node.id)!.y + height(node.id)));
    const targetSide = compact && model.edges.some(edge => edge.target === node.id &&
      (outgoing.get(edge.source)!.length > 1 || positions.get(node.id)!.y > positions.get(edge.source)!.y + height(edge.source)));
    return {id: node.id, position: positions.get(node.id)!, sourcePosition: sourceSide ? Position.Left : Position.Bottom,
      targetPosition: targetSide ? Position.Left : Position.Top, ariaLabel: `${node.title}. ${nodeLines(node, options.expanded).join(" ")}`,
      data: {label: <div className="space-y-2 text-left"><OtsBadge size="compact" tone={tone}>{node.title}</OtsBadge>
        {node.messages?.length ? node.messages.map((message, index) => <DecisionMessage key={index} message={message} {...options} />)
          : node.lines.map((line, index) => <p key={index} className="break-words text-sm leading-5">{line}</p>)}
      </div>}, style: {width: compact ? 260 : node.kind === "reason" ? 400 : 280, padding: 14, borderRadius: 12,
        background: "var(--ots-surface)", color: "var(--ots-text)",
        borderColor: tone === "danger" ? "var(--ots-danger-outline)" : tone === "success" ? "var(--ots-success-outline)" : "var(--ots-outline-variant)"}};
  });
  const edges: Edge[] = model.edges.map(edge => ({id: JSON.stringify([edge.source, edge.target]), source: edge.source, target: edge.target,
    type: "smoothstep", markerEnd: {type: MarkerType.ArrowClosed}, ariaLabel: edge.explanation ?? "Decision connection",
    style: {stroke: edge.status === "rejected" ? "var(--ots-error)" : "var(--ots-primary)", strokeWidth: 2,
      strokeDasharray: edge.status === "rejected" ? "5 4" : undefined}}));
  return {nodes, edges};
};

export const DecisionGraph = ({model, canNavigate, onNavigate, label = "Decision flow", description = "Follow the arrows from inputs to outputs. Dashed branches show alternatives that were not selected. Pan or zoom to inspect the diagram."}: DecisionGraphProps) => {
  const container = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useLayoutEffect(() => {
    const element = container.current;
    if (!element) return;
    const measure = () => {
      const width = element.getBoundingClientRect().width;
      if (width > 0) setCompact(width < 480);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const graph = useMemo(() => buildDecisionGraph(model, compact, {expanded, canNavigate, onNavigate}), [model, compact, expanded, canNavigate, onNavigate]);
  const theme = {
    "--xy-controls-button-background-color": "var(--ots-surface)",
    "--xy-controls-button-background-color-hover": "var(--ots-surface-variant)",
    "--xy-controls-button-color": "var(--ots-text)",
    "--xy-controls-button-color-hover": "var(--ots-text)",
    "--xy-controls-button-border-color": "var(--ots-outline-variant)",
    "--xy-attribution-background-color": "var(--ots-surface)",
  } as CSSProperties;
  return <section aria-label={label} style={theme}>
    {model.nodes.some(node => node.messages?.some(message => message.detail || message.facts?.length)) &&
      <button type="button" aria-expanded={expanded} className="mb-2 text-sm font-semibold text-[var(--ots-primary)]"
        onClick={() => setExpanded(value => !value)}>{expanded ? "Hide supporting details" : "Show supporting details"}</button>}
    <div ref={container} className="overflow-hidden rounded-xl border border-[var(--ots-outline-variant)] bg-[var(--ots-surface-variant)]" style={{height: Math.min(compact ? 1100 : 1000, Math.max(480, ...graph.nodes.map(node => node.position.y + 180)))}}>
      <ReactFlow key={`${compact ? "compact" : "wide"}-${expanded}`} nodes={graph.nodes} edges={graph.edges} fitView fitViewOptions={{padding: 0.12, minZoom: compact ? 0.75 : 0.55, maxZoom: 1}}
        nodesDraggable={false} nodesConnectable={false} edgesFocusable={false} deleteKeyCode={null}
        minZoom={0.2} maxZoom={2} panOnDrag zoomOnScroll zoomOnPinch zoomOnDoubleClick preventScrolling>
        <Background color="var(--ots-outline-variant)" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
    <p className="mt-2 text-xs text-[var(--ots-text-muted)]">{description}</p>
  </section>;
};
